import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { useGame } from "../context/GameContext";
import api from "../lib/api";
import { ASSETS } from "../lib/assets";
import Mascot from "../components/Mascot";
import SquishyButton from "../components/SquishyButton";
import { speak } from "../lib/speech";
import { sound } from "../lib/sounds";
import { Coins, LogOut, LayoutDashboard, UserPlus } from "lucide-react";

const CARD_COLORS = ["#38BDF8", "#F472B6", "#4ADE80", "#FB923C", "#A78BFA", "#22D3EE"];

export default function ChildPicker() {
  const { logout } = useAuth();
  const { selectChild, clearActive, enableAudio } = useGame();
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/profiles").then(({ data }) => setChildren(data)).catch(() => {}).finally(() => setLoaded(true));
    const t = setTimeout(() => speak("Wie gaat er spelen?"), 500);
    return () => clearTimeout(t);
  }, []);

  const play = async (id) => {
    enableAudio(); sound.reward();
    await selectChild(id);
    navigate("/spelen");
  };

  const doLogout = async () => { clearActive(); await logout(); navigate("/ouders"); };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-200 via-sky-100 to-emerald-50 flex flex-col items-center px-5 py-8 overflow-hidden">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {[0, 1, 2].map((i) => (
          <div key={i} className="absolute text-6xl opacity-70 animate-cloud-drift" style={{ top: `${8 + i * 22}%`, animationDelay: `${i * 12}s` }}>☁️</div>
        ))}
      </div>

      <div className="z-10 flex flex-col items-center">
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-sky-600 drop-shadow-sm">SpeelSlim</h1>
        <Mascot mood="idle" size={110} message="Wie gaat er spelen?" />
      </div>

      <div className="z-10 mt-4 w-full max-w-2xl">
        {loaded && children.length === 0 ? (
          <div className="rounded-3xl bg-white p-8 text-center shadow-lg">
            <p className="font-display text-xl font-bold text-slate-700">Nog geen kinderen</p>
            <p className="mt-1 text-slate-400">Voeg eerst kinderen toe in het ouder-dashboard.</p>
            <SquishyButton data-testid="picker-add-child-btn" onClick={() => navigate("/ouders/dashboard")}
              color="#4ADE80" edge="#16A34A" className="mt-4 inline-flex items-center gap-2">
              <UserPlus className="h-5 w-5" /> Kind toevoegen
            </SquishyButton>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            {children.map((c, i) => (
              <motion.button
                key={c.id} data-testid={`pick-child-${c.id}`}
                initial={{ scale: 0, y: 20 }} animate={{ scale: 1, y: 0 }} transition={{ delay: i * 0.06, type: "spring", stiffness: 200 }}
                whileHover={{ scale: 1.05, y: -4 }} whileTap={{ scale: 0.96 }}
                onClick={() => play(c.id)}
                className="flex flex-col items-center gap-2 rounded-[1.75rem] bg-white p-5 shadow-xl"
              >
                <div className="flex h-24 w-24 items-center justify-center rounded-full shadow-inner"
                     style={{ background: `${CARD_COLORS[i % CARD_COLORS.length]}22` }}>
                  <img src={ASSETS.lunaIdle} alt="" className="h-20 w-20 object-contain" />
                </div>
                <p className="font-display text-xl font-bold text-slate-800">{c.name}</p>
                <div className="flex items-center gap-3 text-sm font-bold text-slate-400">
                  <span>{c.age} jaar</span>
                  <span className="flex items-center gap-1"><Coins className="h-4 w-4 text-yellow-500" />{c.coins}</span>
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </div>

      <div className="z-10 mt-8 flex gap-3">
        <button data-testid="picker-dashboard-btn" onClick={() => navigate("/ouders/dashboard")}
          className="flex items-center gap-2 rounded-full bg-white/90 px-5 py-3 font-display font-bold text-slate-600 shadow">
          <LayoutDashboard className="h-5 w-5 text-sky-500" /> Ouder dashboard
        </button>
        <button data-testid="picker-logout-btn" onClick={doLogout}
          className="flex items-center gap-2 rounded-full bg-white/90 px-5 py-3 font-display font-bold text-slate-600 shadow">
          <LogOut className="h-5 w-5 text-red-400" /> Uitloggen
        </button>
      </div>
    </div>
  );
}
